import { json, type LoaderFunction } from '@remix-run/node'
import {
	useLoaderData,
	useSearchParams,
	Link,
	useFetcher,
	Form,
	useSubmit,
} from '@remix-run/react'
import { useRef, useEffect } from 'react'
import { Input } from '#app/components/ui/input'
import { prisma } from '#app/utils/db.server.ts'
import type { Cosmetic } from '@prisma/client'
import { slugify } from '#app/utils/misc.tsx'

const ITEMS_PER_PAGE = 40

type LoaderData = {
	cosmetics: Cosmetic[]
	totalCount: number
	page: number
}

export const loader: LoaderFunction = async ({ request }) => {
	const url = new URL(request.url)
	const searchTerm = url.searchParams.get('search') || ''
	const filterType = url.searchParams.get('type') || 'outfit'
	const page = parseInt(url.searchParams.get('page') || '1', 10)

	const cosmetics = await prisma.cosmetic.findMany({
		where: {
			type: filterType === 'all' ? undefined : filterType,
			name: {
				contains: searchTerm,
				// Remove the 'mode: 'insensitive'' option
			},
			images: {
				not: '',
			},
		},
		orderBy: {
			added: 'desc',
		},
		distinct: ['name'],
		take: ITEMS_PER_PAGE,
		skip: (page - 1) * ITEMS_PER_PAGE,
	})

	const totalCount = await prisma.cosmetic.count({
		where: {
			type: filterType === 'all' ? undefined : filterType,
			name: {
				contains: searchTerm,
				// Remove the 'mode: 'insensitive'' option
			},
			images: {
				not: '',
			},
		},
	})

	return json<LoaderData>({ cosmetics, totalCount, page })
}

const popularTypes = [
	{ type: 'outfit', icon: '👕', label: 'Outfits' },
	{ type: 'backpack', icon: '🎒', label: 'Back Blings' },
	{ type: 'pickaxe', icon: '⛏️', label: 'Pickaxes' },
	{ type: 'glider', icon: '🪂', label: 'Gliders' },
	{ type: 'emote', icon: '💃', label: 'Emotes' },
	{ type: 'wrap', icon: '🎨', label: 'Wraps' },
]

export default function CosmeticsIndex() {
	const { cosmetics, totalCount, page } = useLoaderData<LoaderData>()
	const [searchParams, setSearchParams] = useSearchParams()
	const fetcher = useFetcher<LoaderData>()
	const loadingRef = useRef(false)
	const submit = useSubmit()

	const allCosmetics = [...cosmetics, ...(fetcher.data?.cosmetics || [])]

	const loadMore = () => {
		if (
			!loadingRef.current &&
			fetcher.state === 'idle' &&
			allCosmetics.length < totalCount
		) {
			loadingRef.current = true
			const nextPage = page + 1
			fetcher.load(
				`/cosmetics?index&page=${nextPage}&${searchParams.toString()}`,
			)
		}
	}

	useEffect(() => {
		if (fetcher.data) {
			loadingRef.current = false
		}
	}, [fetcher.data])

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const searchTerm = event.target.value
		const newParams = new URLSearchParams(searchParams)
		newParams.set('search', searchTerm)
		newParams.set('page', '1')
		setSearchParams(newParams)
		submit(event.currentTarget.form)
	}

	return (
		<div className="container mx-auto px-4 py-8">
			<h1 className="mb-4 text-3xl font-bold">Fortnite Cosmetics</h1>

			<Form method="get" className="mb-6 flex items-center space-x-4">
				<Input
					type="text"
					name="search"
					placeholder="Search cosmetics..."
					defaultValue={searchParams.get('search') || ''}
					onChange={handleSearch}
					className="max-w-sm"
				/>
				<select
					name="type"
					defaultValue={searchParams.get('type') || 'outfit'}
					onChange={e => submit(e.currentTarget.form)}
					className="rounded-md border border-gray-300 px-3 py-2"
				>
					<option value="all">All Types</option>
					{popularTypes.map(({ type, label }) => (
						<option key={type} value={type}>
							{label}
						</option>
					))}
				</select>
			</Form>

			{/* Popular Types Bar */}
			<div className="mb-6 flex space-x-4 overflow-x-auto">
				{popularTypes.map(({ type, icon, label }) => (
					<Link
						key={type}
						to={`?type=${type}`}
						className={`flex items-center rounded-md p-2 transition-colors ${
							searchParams.get('type') === type
								? 'bg-purple-500 text-white'
								: 'hover:bg-gray-200'
						}`}
					>
						<span className="mr-2 text-2xl">{icon}</span>
						<span className="text-sm">{label}</span>
					</Link>
				))}
			</div>

			<div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				{allCosmetics.map(item => {
					let images: { icon?: string } = {}
					try {
						images = JSON.parse(item.images as string)
					} catch (error) {
						console.error('Failed to parse images JSON:', error)
					}
					if (!images || !images.icon) return null
					return (
						<Link
							key={item.id}
							to={`/cosmetics/${slugify(item.name)}`}
							className="overflow-hidden rounded-lg border bg-white shadow-sm transition-transform hover:scale-105"
						>
							<div>
								<img
									alt={item.name}
									className="h-auto w-full object-cover"
									height="300"
									width="100%"
									src={images.icon}
									style={{ aspectRatio: '300/300', objectFit: 'cover' }}
								/>
								<div className="p-4">
									<h3 className="text-lg font-semibold">{item.name}</h3>
									<p className="mt-2 text-sm text-gray-600">
										{item.description}
									</p>
									<p className="mt-2 text-sm font-medium text-gray-900">
										Type: {item.type}
									</p>
									<p className="mt-1 text-sm font-medium text-gray-900">
										Rarity: {item.rarity}
									</p>
								</div>
							</div>
						</Link>
					)
				})}
			</div>
			{allCosmetics.length < totalCount && (
				<div className="mt-8 flex justify-center">
					<button
						onClick={loadMore}
						disabled={loadingRef.current}
						className="rounded bg-purple-500 px-8 py-3 text-lg font-semibold text-white disabled:bg-purple-300"
					>
						{loadingRef.current ? 'Loading...' : 'Load More'}
					</button>
				</div>
			)}
		</div>
	)
}
